import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { apiClient } from "../utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Card } from "@/components/ui/card";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import {
  Plus,
  Calendar as CalendarIcon,
  BellRing,
  CheckCircle,
  XCircle,
  RefreshCw,
  Mail,
  MessageSquare,
  Users,
  Clock,
  Pencil,
  Trash2,
  Search,
  Filter,
  ChevronDown,
  SlidersHorizontal,
  CalendarCheck,
  CalendarX,
  ArrowUpDown,
  Eye,
} from "lucide-react";
import { toast } from "sonner";
import {
  format,
  isPast,
  isFuture,
  isToday,
  formatDistanceToNow,
} from "date-fns";

/* ─────────────────────────────────────────────
   Tiny helpers
───────────────────────────────────────────── */
const STATUS = {
  upcoming: {
    label: "Upcoming",
    color: "bg-violet-100 text-violet-700 border-violet-200",
  },
  today: {
    label: "Today",
    color: "bg-amber-100  text-amber-700  border-amber-200",
  },
  past: {
    label: "Past",
    color: "bg-slate-100  text-slate-500  border-slate-200",
  },
};

function getMeetingStatus(dateStr) {
  const d = new Date(dateStr);
  if (isToday(d)) return "today";
  if (isPast(d)) return "past";
  return "upcoming";
}

function toLocalDatetimeValue(isoStr) {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/* ─────────────────────────────────────────────
   Empty state illustration (SVG inline)
───────────────────────────────────────────── */
function EmptyIllustration() {
  return (
    <svg
      viewBox="0 0 200 160"
      className="w-48 h-36 mx-auto mb-4 opacity-60"
      fill="none"
    >
      <rect x="30" y="20" width="140" height="120" rx="10" fill="#EDE9FE" />
      <rect
        x="44"
        y="14"
        width="112"
        height="112"
        rx="8"
        fill="#F5F3FF"
        stroke="#C4B5FD"
        strokeWidth="1.5"
      />
      <rect x="58" y="30" width="84" height="8" rx="4" fill="#DDD6FE" />
      <rect x="58" y="46" width="56" height="6" rx="3" fill="#EDE9FE" />
      <rect x="58" y="62" width="70" height="6" rx="3" fill="#EDE9FE" />
      <rect x="58" y="78" width="40" height="6" rx="3" fill="#EDE9FE" />
      <circle cx="148" cy="116" r="20" fill="#7C3AED" />
      <path
        d="M141 116 h14 M148 109 v14"
        stroke="white"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* ─────────────────────────────────────────────
   Meeting Form (shared by Create & Edit)
───────────────────────────────────────────── */
function MeetingForm({ initial, users, onSubmit, loading, mode = "create" }) {
  const [form, setForm] = useState({
    client_lead_name: "",
    client_lead_id: "",
    purpose: "",
    date_time: "",
    assigned_staff: [],
    ...initial,
    date_time: initial?.date_time
      ? toLocalDatetimeValue(initial.date_time)
      : "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleStaff = (id) => {
    set(
      "assigned_staff",
      form.assigned_staff.includes(id)
        ? form.assigned_staff.filter((x) => x !== id)
        : [...form.assigned_staff, id],
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ ...form, date_time: new Date(form.date_time).toISOString() });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Client / Lead name */}
      <div className="space-y-1.5">
        <Label htmlFor="client_lead_name" className="text-sm font-medium">
          Client / Lead Name <span className="text-rose-500">*</span>
        </Label>
        <Input
          id="client_lead_name"
          placeholder="e.g. Acme Corp"
          value={form.client_lead_name}
          onChange={(e) => set("client_lead_name", e.target.value)}
          required
          className="h-10"
        />
      </div>

      {/* Purpose */}
      <div className="space-y-1.5">
        <Label htmlFor="purpose" className="text-sm font-medium">
          Purpose <span className="text-rose-500">*</span>
        </Label>
        <Input
          id="purpose"
          placeholder="e.g. Project kickoff, Proposal discussion"
          value={form.purpose}
          onChange={(e) => set("purpose", e.target.value)}
          required
          className="h-10"
        />
      </div>

      {/* Date & Time */}
      <div className="space-y-1.5">
        <Label htmlFor="date_time" className="text-sm font-medium">
          Date &amp; Time <span className="text-rose-500">*</span>
        </Label>
        <DateTimePicker
          value={form.date_time}
          onChange={(val) => set("date_time", val)}
          placeholder="Select meeting date and time"
        />
      </div>

      <Button
        type="submit"
        className="w-full h-10 bg-violet-600 hover:bg-violet-700"
        disabled={loading}
      >
        {loading ? (
          <>
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />{" "}
            {mode === "create" ? "Scheduling…" : "Saving…"}
          </>
        ) : mode === "create" ? (
          "Schedule Meeting"
        ) : (
          "Save Changes"
        )}
      </Button>
    </form>
  );
}

/* ─────────────────────────────────────────────
   Detail / View Dialog
───────────────────────────────────────────── */
function MeetingDetailDialog({ meeting, users, open, onClose }) {
  if (!meeting) return null;
  const status = getMeetingStatus(meeting.date_time);
  const statusMeta = STATUS[status];
  const getUserName = (id) => users.find((u) => u.id === id)?.name || id;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="text-xl leading-tight">
              {meeting.client_lead_name}
            </DialogTitle>
            <Badge
              className={`${statusMeta.color} border text-xs shrink-0 mt-0.5`}
            >
              {statusMeta.label}
            </Badge>
          </div>
          <DialogDescription>{meeting.purpose}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Date / time */}
          <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
            <CalendarIcon className="w-4 h-4 text-violet-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium">
                {format(new Date(meeting.date_time), "EEEE, MMMM d, yyyy")}
              </p>
              <p className="text-xs text-muted-foreground">
                {format(new Date(meeting.date_time), "h:mm a")}
                {" · "}
                {status === "past"
                  ? formatDistanceToNow(new Date(meeting.date_time), {
                      addSuffix: true,
                    })
                  : formatDistanceToNow(new Date(meeting.date_time), {
                      addSuffix: true,
                    })}
              </p>
            </div>
          </div>

          {/* Created at */}
          <p className="text-xs text-muted-foreground">
            Scheduled{" "}
            {formatDistanceToNow(new Date(meeting.created_at), {
              addSuffix: true,
            })}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────────────────────────
   Main Page
───────────────────────────────────────────── */
export default function MeetingsPage() {
  const { user } = useAuth();
  const canManage = user?.role === "owner" || user?.role === "pillar";

  /* ── state ── */
  const [meetings, setMeetings] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarConnected, setCalendarConnected] = useState(null);

  // dialogs
  const [createOpen, setCreateOpen] = useState(false);
  const [editMeeting, setEditMeeting] = useState(null); // meeting obj or null
  const [viewMeeting, setViewMeeting] = useState(null);

  // action states
  const [formLoading, setFormLoading] = useState(false);
  const [remindingId, setRemindingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [reminderResults, setReminderResults] = useState({});
  const [testingCalendar, setTestingCalendar] = useState(false);

  // filters & search
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // all | upcoming | today | past
  const [sortOrder, setSortOrder] = useState("asc"); // asc | desc

  /* ── data fetching ── */
  useEffect(() => {
    fetchMeetings();
    fetchUsers();
    fetchCalendarStatus();
  }, []);

  const fetchMeetings = async () => {
    try {
      const { data } = await apiClient.get("/meetings");
      setMeetings(data);
    } catch {
      toast.error("Failed to load meetings");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await apiClient.get("/users");
      setUsers(data);
    } catch {
      console.error("Failed to load users");
    }
  };

  const fetchCalendarStatus = async () => {
    try {
      const { data } = await apiClient.get("/calendar/status");
      setCalendarConnected(data.connected);
    } catch {
      setCalendarConnected(false);
    }
  };

  /* ── CREATE ── */
  const handleCreate = async (formData) => {
    setFormLoading(true);
    try {
      await apiClient.post("/meetings", formData);
      toast.success("Meeting scheduled successfully");
      setCreateOpen(false);
      fetchMeetings();
    } catch (err) {
      console.log(err, "error");
      toast.error(err?.response?.data?.detail || "Failed to schedule meeting");
    } finally {
      setFormLoading(false);
    }
  };

  function isIOS() {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
    );
  }

  const addToAppleCalendar = (meeting) => {
    const icsUrl = `${apiClient.defaults.baseURL}/meetings/${meeting.id}/ics`;
    // Create a temporary anchor and trigger download
    const link = document.createElement("a");
    link.href = icsUrl;
    link.setAttribute("download", `meeting-${meeting.id}.ics`);
    // Include auth header via a fetch + blob approach
    fetch(icsUrl, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        link.href = blobUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
        toast.success("Opening in Apple Calendar…");
      })
      .catch(() => toast.error("Failed to generate calendar file"));
  };

  /* ── EDIT / UPDATE ── */
  const handleEdit = async (formData) => {
    setFormLoading(true);
    try {
      await apiClient.patch(`/meetings/${editMeeting.id}`, formData);
      toast.success("Meeting updated");
      setEditMeeting(null);
      fetchMeetings();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update meeting");
    } finally {
      setFormLoading(false);
    }
  };

  /* ── DELETE ── */
  const handleDelete = async (meetingId) => {
    setDeletingId(meetingId);
    try {
      await apiClient.delete(`/meetings/${meetingId}`);
      toast.success("Meeting deleted");
      setMeetings((prev) => prev.filter((m) => m.id !== meetingId));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete meeting");
    } finally {
      setDeletingId(null);
    }
  };

  /* ── REMIND ── */
  const sendReminderNow = async (meeting) => {
    setRemindingId(meeting.id);
    try {
      const { data } = await apiClient.post(
        `/meetings/${meeting.id}/remind-now`,
      );
      setReminderResults((prev) => ({ ...prev, [meeting.id]: data.results }));

      const emailsSent = data.results.email.filter((r) => r.success).length;
      const smsSent = data.results.sms.filter((r) => r.success).length;
      const emailFailed = data.results.email.filter((r) => !r.success).length;
      const smsFailed = data.results.sms.filter((r) => !r.success).length;

      if (emailFailed === 0 && smsFailed === 0) {
        toast.success(
          `Reminders sent — ${emailsSent} email${emailsSent !== 1 ? "s" : ""}, ${smsSent} SMS`,
        );
      } else {
        toast.warning(
          `Sent ${emailsSent} emails, ${smsSent} SMS. Failed: ${emailFailed} email, ${smsFailed} SMS`,
        );
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send reminders");
    } finally {
      setRemindingId(null);
    }
  };

  /* ── CALENDAR TEST ── */
  const testCalendarEvent = async () => {
    setTestingCalendar(true);
    try {
      const { data } = await apiClient.post("/test/calendar-event");
      toast.success(
        <span>
          Calendar event created!{" "}
          <a
            href={data.event_link}
            target="_blank"
            rel="noreferrer"
            className="underline font-medium"
          >
            Open in Google Calendar →
          </a>
        </span>,
        { duration: 8000 },
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Calendar test failed — is Google Calendar connected?",
      );
    } finally {
      setTestingCalendar(false);
    }
  };

  const connectCalendar = async () => {
    try {
      const { data } = await apiClient.get("/calendar/connect");
      window.open(data.authorization_url, "_blank");
      toast.info(
        "Complete the Google sign-in in the new tab, then refresh this page",
      );
    } catch {
      toast.error("Failed to initiate Google Calendar connection");
    }
  };

  /* ── helpers ── */
  const getUserName = (id) => users.find((u) => u.id === id)?.name || id;

  /* ── filtered + sorted list ── */
  const filtered = meetings
    .filter((m) => {
      const matchSearch =
        m.client_lead_name.toLowerCase().includes(search.toLowerCase()) ||
        m.purpose.toLowerCase().includes(search.toLowerCase());
      const matchStatus =
        filterStatus === "all" ||
        getMeetingStatus(m.date_time) === filterStatus;
      return matchSearch && matchStatus;
    })
    .sort((a, b) => {
      const diff = new Date(a.date_time) - new Date(b.date_time);
      return sortOrder === "asc" ? diff : -diff;
    });

  /* ── summary counts ── */
  const counts = {
    all: meetings.length,
    upcoming: meetings.filter(
      (m) => getMeetingStatus(m.date_time) === "upcoming",
    ).length,
    today: meetings.filter((m) => getMeetingStatus(m.date_time) === "today")
      .length,
    past: meetings.filter((m) => getMeetingStatus(m.date_time) === "past")
      .length,
  };

  /* ─────────────────────────── RENDER ─────────────────────────── */
  return (
    <div className="space-y-5 lg:space-y-6" data-testid="meetings-page">
      {/* ══ Header ══ */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold tracking-tight">
            Meetings
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Schedule, track, and manage all client &amp; lead meetings
          </p>
        </div>

        <div className="flex flex-wrap gap-2 shrink-0">
          {calendarConnected === false && (
            <Button variant="outline" size="sm" onClick={connectCalendar}>
              <CalendarIcon className="w-4 h-4 mr-2" />
              Connect Calendar
            </Button>
          )}
          {calendarConnected === true && (
            <Button
              variant="outline"
              size="sm"
              onClick={testCalendarEvent}
              disabled={testingCalendar}
            >
              {testingCalendar ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CalendarCheck className="w-4 h-4 mr-2" />
              )}
              Test Calendar
            </Button>
          )}

          {canManage && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-violet-600 hover:bg-violet-700"
                  data-testid="schedule-meeting-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Schedule Meeting
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Schedule New Meeting</DialogTitle>
                  <DialogDescription>
                    Fill in the details to create a new meeting.
                  </DialogDescription>
                </DialogHeader>
                <MeetingForm
                  users={users}
                  onSubmit={handleCreate}
                  loading={formLoading}
                  mode="create"
                />
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* ══ Calendar status banner ══ */}
      {calendarConnected !== null && (
        <div
          className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-sm ${
            calendarConnected
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-amber-50  border-amber-200  text-amber-800"
          }`}
        >
          {calendarConnected ? (
            <CheckCircle className="w-4 h-4 shrink-0" />
          ) : (
            <XCircle className="w-4 h-4 shrink-0" />
          )}
          {calendarConnected
            ? "Google Calendar connected — new meetings will automatically create calendar events."
            : "Google Calendar not connected — connect it to auto-create events when meetings are scheduled."}
        </div>
      )}

      {/* ══ Summary tiles ══ */}
      {!loading && meetings.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            {
              key: "all",
              label: "Total",
              icon: CalendarIcon,
              color: "text-slate-600",
              bg: "bg-slate-50",
            },
            {
              key: "today",
              label: "Today",
              icon: Clock,
              color: "text-amber-600",
              bg: "bg-amber-50",
            },
            {
              key: "upcoming",
              label: "Upcoming",
              icon: CalendarCheck,
              color: "text-violet-600",
              bg: "bg-violet-50",
            },
            {
              key: "past",
              label: "Past",
              icon: CalendarX,
              color: "text-slate-400",
              bg: "bg-slate-50",
            },
          ].map(({ key, label, icon: Icon, color, bg }) => (
            <button
              key={key}
              onClick={() => setFilterStatus(key)}
              className={`p-4 rounded-xl border text-left transition-all
                ${filterStatus === key ? "ring-2 ring-violet-500 border-violet-200" : "hover:border-slate-300"}
                ${bg}`}
            >
              <Icon className={`w-4 h-4 mb-2 ${color}`} />
              <p className={`text-2xl font-bold ${color}`}>{counts[key]}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
            </button>
          ))}
        </div>
      )}

      {/* ══ Filters & search bar ══ */}
      {!loading && meetings.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3">
          {/* search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by client or purpose…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10"
            />
          </div>

          {/* status filter */}
          <div className="flex gap-2">
            {[
              { value: "all", label: "All" },
              { value: "today", label: "Today" },
              { value: "upcoming", label: "Upcoming" },
              { value: "past", label: "Past" },
            ].map((opt) => (
              <Button
                key={opt.value}
                size="sm"
                variant={filterStatus === opt.value ? "default" : "outline"}
                className={
                  filterStatus === opt.value
                    ? "bg-violet-600 hover:bg-violet-700"
                    : ""
                }
                onClick={() => setFilterStatus(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </div>

          {/* sort toggle */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => setSortOrder((o) => (o === "asc" ? "desc" : "asc"))}
            className="gap-1.5"
          >
            <ArrowUpDown className="w-3.5 h-3.5" />
            {sortOrder === "asc" ? "Oldest first" : "Newest first"}
          </Button>
        </div>
      )}

      {/* ══ Meeting list ══ */}
      {loading ? (
        <div className="grid gap-3">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="p-6">
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-muted rounded w-1/3" />
                <div className="h-3 bg-muted rounded w-2/3" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </div>
            </Card>
          ))}
        </div>
      ) : meetings.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <EmptyIllustration />
          <p className="text-lg font-semibold text-foreground mb-1">
            No meetings yet
          </p>
          <p className="text-sm text-muted-foreground mb-6">
            Schedule your first meeting to get started.
          </p>
          {canManage && (
            <Button
              className="bg-violet-600 hover:bg-violet-700"
              onClick={() => setCreateOpen(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Schedule Meeting
            </Button>
          )}
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center border-dashed">
          <Search className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">
            No meetings match your search or filter.
          </p>
          <Button
            variant="ghost"
            size="sm"
            className="mt-3"
            onClick={() => {
              setSearch("");
              setFilterStatus("all");
            }}
          >
            Clear filters
          </Button>
        </Card>
      ) : (
        <div className="grid gap-3">
          {filtered.map((meeting) => {
            const status = getMeetingStatus(meeting.date_time);
            const statusMeta = STATUS[status];
            const results = reminderResults[meeting.id];
            const isDeleting = deletingId === meeting.id;
            const isReminding = remindingId === meeting.id;

            return (
              <Card
                key={meeting.id}
                className={`p-5 transition-all hover:shadow-md border
                  ${status === "past" ? "opacity-75" : ""}
                  ${status === "today" ? "border-amber-300 bg-amber-50/30" : ""}
                  ${status === "upcoming" ? "border-violet-200" : ""}
                `}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  {/* ── Left: meeting info ── */}
                  <div className="flex-1 min-w-0 space-y-2.5">
                    {/* Title row */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base font-semibold truncate">
                        {meeting.client_lead_name}
                      </h3>
                      <Badge
                        variant="outline"
                        className={`text-xs shrink-0 ${statusMeta.color}`}
                      >
                        {statusMeta.label}
                      </Badge>
                      {status === "today" && (
                        <span className="text-xs font-medium text-amber-600 animate-pulse">
                          ● Live today
                        </span>
                      )}
                    </div>

                    {/* Meta */}
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <p className="flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5 shrink-0 text-violet-500" />
                        <span className="font-medium text-foreground">
                          {format(new Date(meeting.date_time), "PPp")}
                        </span>
                        <span className="text-xs">
                          (
                          {formatDistanceToNow(new Date(meeting.date_time), {
                            addSuffix: true,
                          })}
                          )
                        </span>
                      </p>

                      <p>
                        <span className="font-medium text-foreground">
                          Purpose:{" "}
                        </span>
                        {meeting.purpose}
                      </p>

                      {meeting.assigned_staff?.length > 0 && (
                        <p className="flex items-center gap-2 flex-wrap">
                          <Users className="w-3.5 h-3.5 shrink-0" />
                          {meeting.assigned_staff.map((id) => (
                            <span
                              key={id}
                              className="bg-violet-100 text-violet-700 text-xs px-1.5 py-0.5 rounded-md"
                            >
                              {getUserName(id)}
                            </span>
                          ))}
                        </p>
                      )}
                    </div>

                    {/* Reminder delivery results */}
                    {results && (
                      <div className="mt-2 p-3 bg-muted/60 rounded-lg space-y-1.5 border">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">
                          Last reminder delivery
                        </p>
                        {results.email.map((r, i) => (
                          <div
                            key={i}
                            className="flex items-center gap-2 text-xs"
                          >
                            {r.success ? (
                              <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                            ) : (
                              <XCircle className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                            )}
                            <Mail className="w-3 h-3 text-muted-foreground shrink-0" />
                            <span
                              className={
                                r.success ? "text-emerald-700" : "text-rose-700"
                              }
                            >
                              {r.to} — {r.success ? "delivered" : r.error}
                            </span>
                          </div>
                        ))}
                        {results.sms.map((r, i) => (
                          <div
                            key={i}
                            className="flex items-center gap-2 text-xs"
                          >
                            {r.success ? (
                              <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                            ) : (
                              <XCircle className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                            )}
                            <MessageSquare className="w-3 h-3 text-muted-foreground shrink-0" />
                            <span
                              className={
                                r.success ? "text-emerald-700" : "text-rose-700"
                              }
                            >
                              {r.to} — {r.success ? "delivered" : r.error}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* ── Right: action buttons ── */}
                  <div className="flex sm:flex-col gap-2 shrink-0">
                    {/* View details */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setViewMeeting(meeting)}
                      className="flex-1 sm:flex-none justify-start sm:justify-center text-muted-foreground hover:text-foreground"
                    >
                      <Eye className="w-4 h-4 sm:mr-0 mr-2" />
                      <span className="sm:hidden">View</span>
                    </Button>
                    {/* iOS Apple Calendar info banner */}
                    {isIOS() && (
                      <div className="flex items-center gap-3 px-4 py-3 rounded-lg border text-sm bg-slate-50 border-slate-200 text-slate-700">
                        <svg
                          className="w-4 h-4 shrink-0"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                        >
                          <rect x="3" y="4" width="18" height="18" rx="3" />
                          <path d="M3 9h18M8 2v4M16 2v4" />
                        </svg>
                        iOS detected — tap the calendar icon on any meeting to
                        add it to Apple Calendar.
                      </div>
                    )}
                    {/* Apple Calendar — iOS only */}
                    {isIOS() && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => addToAppleCalendar(meeting)}
                        className="flex-1 sm:flex-none justify-start sm:justify-center text-slate-600 hover:text-slate-800"
                        title="Add to Apple Calendar"
                      >
                        {/* Apple Calendar icon (SF-style) */}
                        <svg
                          className="w-4 h-4 sm:mr-0 mr-2"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                        >
                          <rect x="3" y="4" width="18" height="18" rx="3" />
                          <path d="M3 9h18" />
                          <path d="M8 2v4M16 2v4" />
                          <path
                            d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                          />
                        </svg>
                        <span className="sm:hidden">Apple Calendar</span>
                      </Button>
                    )}
                    {canManage && (
                      <>
                        {/* Send reminder */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => sendReminderNow(meeting)}
                          disabled={isReminding}
                          className="flex-1 sm:flex-none justify-start sm:justify-center"
                        >
                          {isReminding ? (
                            <RefreshCw className="w-4 h-4 animate-spin sm:mr-0 mr-2" />
                          ) : (
                            <BellRing className="w-4 h-4 sm:mr-0 mr-2" />
                          )}
                          <span className="sm:hidden">
                            {isReminding ? "Sending…" : "Remind"}
                          </span>
                        </Button>

                        {/* Edit */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditMeeting(meeting)}
                          className="flex-1 sm:flex-none justify-start sm:justify-center"
                        >
                          <Pencil className="w-4 h-4 sm:mr-0 mr-2" />
                          <span className="sm:hidden">Edit</span>
                        </Button>

                        {/* Delete */}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={isDeleting}
                              className="flex-1 sm:flex-none justify-start sm:justify-center text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200"
                            >
                              {isDeleting ? (
                                <RefreshCw className="w-4 h-4 animate-spin sm:mr-0 mr-2" />
                              ) : (
                                <Trash2 className="w-4 h-4 sm:mr-0 mr-2" />
                              )}
                              <span className="sm:hidden">Delete</span>
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Delete this meeting?
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                The meeting with{" "}
                                <strong>{meeting.client_lead_name}</strong> on{" "}
                                {format(new Date(meeting.date_time), "PPp")}{" "}
                                will be permanently removed. This action cannot
                                be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(meeting.id)}
                                className="bg-rose-600 hover:bg-rose-700"
                              >
                                Delete Meeting
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* ══ Edit Dialog ══ */}
      <Dialog
        open={!!editMeeting}
        onOpenChange={(open) => !open && setEditMeeting(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Meeting</DialogTitle>
            <DialogDescription>
              Update the details for this meeting.
            </DialogDescription>
          </DialogHeader>
          {editMeeting && (
            <MeetingForm
              initial={editMeeting}
              users={users}
              onSubmit={handleEdit}
              loading={formLoading}
              mode="edit"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* ══ View / Detail Dialog ══ */}
      <MeetingDetailDialog
        meeting={viewMeeting}
        users={users}
        open={!!viewMeeting}
        onClose={() => setViewMeeting(null)}
      />
    </div>
  );
}
